import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { OtapUpgradeService } from '@services/otap-upgrade/otap.upgrade.service';
import { OtapUpgradesDatasource } from '@app/global-administration/otap-upgrades/otap-upgrades.datasource';
import { tap } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  AddJobDialogData,
  CreateJobsDialogComponent,
  DialogResult,
  FORM_TYPE
} from '@app/global-administration/otap-upgrades/create-jobs-dialog/create-jobs-dialog.component';
import { AddJobDTO, IOtapUpgradeDTO, JobTaskTemplateMap } from '@app/shared/models/otap.upgrade.interface';
import { Observable, switchMap } from 'rxjs';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatFabButton, MatMiniFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-otap-upgrades',
  templateUrl: './otap-upgrades.component.html',
  styleUrls: ['./otap-upgrades.component.scss'],
  imports: [
    MatProgressSpinner,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    MatFabButton,
    MatTooltip,
    MatIcon,
    MatMiniFabButton
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalAdministrationOtapUpgradesComponent implements OnInit, AfterViewInit {
  readonly displayedColumns = ['id', 'name', 'jobTaskTemplateName', 'status', 'createdDate', 'createdBy', 'actions'];
  private readonly route = inject(ActivatedRoute);
  private readonly otapUpgradeService = inject(OtapUpgradeService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly dataSource = new OtapUpgradesDatasource(this.otapUpgradeService);
  readonly loading = toSignal(this.dataSource.loading$, { initialValue: false });
  readonly counter = toSignal(this.dataSource.counter$, { initialValue: 0 });
  readonly jobs = toSignal(this.dataSource.data$, { initialValue: [] });
  readonly paginator = viewChild.required(MatPaginator);

  ngOnInit(): void {
    this.dataSource.loadOtapUpgrades(0, 15);
  }

  ngAfterViewInit(): void {
    this.paginator()
      .page.pipe(
        tap(() => this.loadOtapUpgradesPage()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  loadOtapUpgradesPage(): void {
    this.dataSource.updatePaginatedData(this.paginator().pageIndex, this.paginator().pageSize);
  }

  addJobs(): void {
    this.openAddJobsDialog(this.dialog).subscribe((result: DialogResult) => {
      if (result) {
        const payload: AddJobDTO = {
          name: result.formValue.name,
          jobTaskTemplate: result.formValue.jobTaskTemplate
        };
        this.otapUpgradeService.addJob(payload).subscribe({
          next: () => {
            this.dataSource.loadOtapUpgrades(this.paginator().pageIndex, this.paginator().pageSize);
          },
          error: (error) => {
            console.error('There was an error adding the job', error);
          }
        });
      }
    });
  }

  private openAddJobsDialog(dialog: MatDialog): Observable<DialogResult | undefined> {
    const config = new MatDialogConfig<AddJobDialogData>();

    return this.otapUpgradeService.getJobTemplates().pipe(
      switchMap((jobTemplates: JobTaskTemplateMap) => {
        config.data = {
          mode: FORM_TYPE.ADD,
          jobTaskTemplate: jobTemplates
        };
        config.autoFocus = true;
        config.width = '600px';
        const dialogRef = dialog.open(CreateJobsDialogComponent, config);
        return dialogRef.afterClosed();
      })
    );
  }

  onJobClicked(row: IOtapUpgradeDTO): void {
    this.otapUpgradeService.updateSelectedJob(row);
    this.router.navigate([row.id, 'job-information'], {
      relativeTo: this.route
    });
  }

  deleteJob(job: IOtapUpgradeDTO): void {
    console.log('Delete job', job);
    if (!confirm(`Job '${job.name}' is going to be deleted. This cannot be undone! Continue?`)) {
      return;
    }
    this.otapUpgradeService.deleteJob(job.id).subscribe({
      next: () => {
        this.dataSource.loadOtapUpgrades(this.paginator().pageIndex, this.paginator().pageSize);
      },
      error: (error) => {
        console.error('There was an error deleting the job', error);
      }
    });
  }
}
